import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, relTime } from '../lib/api'
import { cm } from '../lib/cm'

const PAGE = 50
const VIEWS = [
  { key: '', label: 'All' },
  { key: 'in', label: 'Incoming' },
  { key: 'out', label: 'Outgoing' },
  { key: 'bounces', label: 'Bounces' },
]

/**
 * Every email in and out of the connected mailboxes: the Inbox and Sent folders mirrored from
 * cPanel, mail sent from this panel, and every campaign email.
 */
export function Mail() {
  const [params, setParams] = useSearchParams()
  const mailbox = params.get('mailbox') ?? ''
  const view = params.get('direction') ?? ''
  const [search, setSearch] = useState(params.get('q') ?? '')
  const q = params.get('q') ?? ''
  const page = Number(params.get('page') ?? 0)

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v); else next.delete(k)
    if (k !== 'page') next.delete('page')
    setParams(next)
  }

  const { data: boxes } = useQuery({ queryKey: ['cm', 'mailboxes'], queryFn: cm.mailboxes })
  const { data, isLoading } = useQuery({
    queryKey: ['cm', 'items', mailbox, view, q, page],
    queryFn: () => cm.items({
      mailbox: mailbox || undefined,
      direction: view === 'in' || view === 'out' ? view : undefined,
      bounces: view === 'bounces' || undefined,
      q: q || undefined, limit: PAGE, offset: page * PAGE,
    }),
    refetchInterval: 30_000,
  })

  const rows = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Mail</h1>
          <p className="lede">Everything received and sent by your connected mailboxes, including every campaign email.</p>
        </div>
        <Link to="/mail/compose" className="btn-solid">Compose</Link>
      </div>

      <div className="toolbar">
        <select value={mailbox} onChange={e => setParam('mailbox', e.target.value)} aria-label="Mailbox">
          <option value="">All mailboxes</option>
          {(boxes?.data ?? []).map(b => <option key={b.id} value={b.id}>{b.email}</option>)}
        </select>
        <div className="segmented" role="tablist">
          {VIEWS.map(v => (
            <button key={v.key || 'all'} role="tab" aria-selected={view === v.key}
                    className={view === v.key ? 'active' : ''} onClick={() => setParam('direction', v.key)}>
              {v.label}
            </button>
          ))}
        </div>
        <form className="row" style={{ flex: '1 1 220px' }} onSubmit={e => { e.preventDefault(); setParam('q', search.trim()) }}>
          <input placeholder="Search subject or address…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="secondary" type="submit">Search</button>
        </form>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 300 }} />}
      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">✉</div>
          <p><strong>No mail here yet</strong></p>
          <p>{(boxes?.data.length ?? 0) === 0
            ? <>Connect a mailbox on the <Link to="/mailboxes">Mailboxes</Link> page first.</>
            : 'New mail appears within a couple of minutes of arriving.'}</p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="card flush">
            <table className="data">
              <thead><tr><th style={{ width: 28 }} /><th>{view === 'out' ? 'To' : 'From / To'}</th><th>Subject</th><th>Mailbox</th><th>Date</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer', fontWeight: !r.is_read && r.direction === 'in' ? 600 : undefined }}>
                    <td title={r.direction === 'in' ? 'Incoming' : 'Outgoing'}>{r.direction === 'in' ? '↓' : '↑'}</td>
                    <td className="small">
                      <Link to={`/mail/${r.id}`}>
                        {r.direction === 'in' ? (r.from_name || r.from) : `To: ${r.to ?? ''}`}
                      </Link>
                    </td>
                    <td className="small truncate" style={{ maxWidth: 420 }}>
                      {r.is_bounce && <span className="badge failed">Bounce</span>}{' '}
                      {r.is_campaign && <span className="badge info">Campaign</span>}{' '}
                      <Link to={`/mail/${r.id}`}>{r.subject || '(no subject)'}</Link>
                      {r.snippet && <span className="muted"> — {r.snippet}</span>}
                    </td>
                    <td className="small muted mono">{r.mailbox}</td>
                    <td className="small muted" title={new Date(r.date).toLocaleString()}>{relTime(r.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <button className="secondary" disabled={page === 0} onClick={() => setParam('page', String(page - 1))}>← Newer</button>
            <span className="small muted">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total.toLocaleString()}</span>
            <button className="secondary" disabled={(page + 1) * PAGE >= total} onClick={() => setParam('page', String(page + 1))}>Older →</button>
          </div>
        </>
      )}
    </div>
  )
}

export function MailDetail() {
  const { id = '' } = useParams()
  const { data, isLoading, error } = useQuery({ queryKey: ['cm', 'item', id], queryFn: () => cm.item(id) })
  const [replying, setReplying] = useState(false)

  if (isLoading) return <div className="skeleton" style={{ height: 400 }} />
  if (error || !data) return <div className="alert error">This email could not be loaded.</div>

  return (
    <div>
      <p><Link to="/mail">← Mail</Link></p>
      <h1 style={{ marginBottom: 4 }}>{data.subject || '(no subject)'}</h1>
      <div className="card" style={{ marginBottom: 16 }}>
        <table className="kv"><tbody>
          <tr><td>From</td><td className="mono small">{data.from_name ? `${data.from_name} <${data.from}>` : data.from}</td></tr>
          <tr><td>To</td><td className="mono small">{data.to}</td></tr>
          {data.cc && <tr><td>Cc</td><td className="mono small">{data.cc}</td></tr>}
          <tr><td>Date</td><td className="small">{new Date(data.date).toLocaleString()}</td></tr>
          <tr><td>Mailbox</td><td className="small">{data.mailbox} · {data.folder} · {data.direction === 'in' ? 'incoming' : 'outgoing'}</td></tr>
          {data.campaign_id && <tr><td>Campaign</td><td><Link to={`/campaigns/${data.campaign_id}`}>{data.campaign_name}</Link></td></tr>}
          {data.attachments && <tr><td>Attachments</td><td className="small">{data.attachments}</td></tr>}
          {data.is_bounce && <tr><td>Bounce</td><td><span className="badge failed">Delivery failed</span> <span className="small muted">The address in this report was marked bounced and will not be mailed again.</span></td></tr>}
        </tbody></table>
      </div>

      {data.direction === 'in' && data.mailbox_id && !replying && (
        <button onClick={() => setReplying(true)} style={{ marginBottom: 16 }}>Reply</button>
      )}
      {replying && data.mailbox_id && (
        <Composer
          initial={{
            mailbox_id: data.mailbox_id,
            to: data.from ?? '',
            subject: data.subject?.toLowerCase().startsWith('re:') ? data.subject : `Re: ${data.subject ?? ''}`,
            reply_to_item_id: data.id,
            quote: data.text ?? '',
          }}
          onSent={() => setReplying(false)}
        />
      )}

      <div className="card flush">
        {data.html
          ? <iframe title="Email body" sandbox="" srcDoc={data.html}
                    style={{ width: '100%', minHeight: 520, border: 0, background: '#fff', borderRadius: 8 }} />
          : <pre style={{ whiteSpace: 'pre-wrap', padding: 16, margin: 0 }}>{data.text || '(no text body)'}</pre>}
      </div>
    </div>
  )
}

export function Compose() {
  const [params] = useSearchParams()
  return (
    <div>
      <p><Link to="/mail">← Mail</Link></p>
      <h1>Compose</h1>
      <Composer initial={{ mailbox_id: params.get('mailbox') ?? '', to: params.get('to') ?? '', subject: '' }} />
    </div>
  )
}

function Composer({ initial, onSent }: {
  initial: { mailbox_id: string; to: string; subject: string; reply_to_item_id?: string; quote?: string }
  onSent?: () => void
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { data: boxes } = useQuery({ queryKey: ['cm', 'mailboxes'], queryFn: cm.mailboxes })
  const [mailbox, setMailbox] = useState(initial.mailbox_id)
  const [to, setTo] = useState(initial.to)
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState(initial.subject)
  const [body, setBody] = useState(initial.quote
    ? `\n\n${initial.quote.split('\n').map(l => `> ${l}`).join('\n')}` : '')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!mailbox && boxes?.data.length) setMailbox(boxes.data[0]!.id)
  }, [boxes, mailbox])

  const split = (s: string) => s.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean)
  const send = useMutation({
    mutationFn: () => cm.send({
      mailbox_id: mailbox, to: split(to), cc: split(cc), subject, text: body,
      reply_to_item_id: initial.reply_to_item_id,
    }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['cm'] })
      if (onSent) onSent(); else navigate('/mail?direction=out')
    },
    onError: e => setError((e as ApiError).message),
  })

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <form onSubmit={e => { e.preventDefault(); send.mutate() }}>
        <div className="field"><label>From</label>
          <select value={mailbox} onChange={e => setMailbox(e.target.value)} required>
            {(boxes?.data ?? []).filter(b => b.sending_enabled && !b.auth_failed)
              .map(b => <option key={b.id} value={b.id}>{b.display_name ? `${b.display_name} <${b.email}>` : b.email}</option>)}
          </select></div>
        <div className="field"><label>To</label><input value={to} onChange={e => setTo(e.target.value)} required placeholder="name@example.com, …" /></div>
        <div className="field"><label>Cc</label><input value={cc} onChange={e => setCc(e.target.value)} /></div>
        <div className="field"><label>Subject</label><input value={subject} onChange={e => setSubject(e.target.value)} required /></div>
        <div className="field"><label>Message</label>
          <textarea rows={12} value={body} onChange={e => setBody(e.target.value)} required /></div>
        {error && <div className="alert error">{error}</div>}
        <div className="row">
          <button type="submit" disabled={send.isPending || !mailbox}>{send.isPending ? 'Sending…' : 'Send'}</button>
          <span className="small muted">Sent through the mailbox's own SMTP and saved to its Sent folder. Counts toward the hourly limit.</span>
        </div>
      </form>
    </div>
  )
}
